import { Buffer } from "buffer";
import { IJkurwaCertificate, models } from "jkurwa";

import {
  CMP_FIRST_KEY_ID_OFFSET,
  CMP_FLAG_OFFSETS,
  CMP_HEADER_VALUE,
  CMP_PAYLOAD_LENGTH,
  CMP_PAYLOAD_OFFSET,
  CMP_SECOND_KEY_ID_OFFSET,
  CMP_STATUS_FOUND,
  CMP_STATUS_OFFSET,
  CMP_TYPE_OFFSET,
  CMP_TYPE_VALUE,
  TAX_NUMBER_PREFIX_PATTERN,
} from "@prostocheck/shared";

import { ICertificateDetails } from "./certificates.models";

/** Builds the CMP "give me the certificates for these key ids" request body. */
export const buildCmpPayload = (keyIds: Buffer[]): Buffer => {
  const payload = Buffer.alloc(CMP_PAYLOAD_LENGTH);

  keyIds[0].copy(payload, CMP_FIRST_KEY_ID_OFFSET);
  (keyIds[1] ?? keyIds[0]).copy(payload, CMP_SECOND_KEY_ID_OFFSET);

  CMP_FLAG_OFFSETS.forEach(offset => {
    payload[offset] = 1;
  });

  payload[CMP_TYPE_OFFSET] = CMP_TYPE_VALUE;
  payload[0] = CMP_HEADER_VALUE;

  return new models.Message({ type: "data", data: payload }).as_asn1();
};

/** `null` when the ЦСК answered "no certificates for this key". */
export const parseCmpResponse = (
  response: Buffer,
): IJkurwaCertificate[] | null => {
  const message = new models.Message(response);

  if (message.info.readInt32LE(CMP_STATUS_OFFSET) !== CMP_STATUS_FOUND) {
    return null;
  }

  const inner = new models.Message(
    message.info.subarray(CMP_PAYLOAD_OFFSET) as Buffer,
  );

  return inner.info.certificate.map(der => new models.Certificate(der));
};

const toIsoDate = (value: number | string | Date | undefined): string | null =>
  value === undefined ? null : new Date(value).toISOString().slice(0, 10);

const canUseFor = (certificate: IJkurwaCertificate, op: string): boolean => {
  try {
    return certificate.canUseFor(op);
  } catch {
    return false;
  }
};

/**
 * The subject serial number is the same source ЦЗО displays, so it is preferred
 * over the `ipn` extension, which not every ЦСК fills in.
 */
export const readCertificateDetails = ({
  cert,
  priv,
}: {
  cert: IJkurwaCertificate;
  priv?: unknown;
}): ICertificateDetails => {
  const { subject, issuer } = cert;
  const ipn = cert.extension.ipn ?? {};
  const validFrom = cert.valid ? new Date(cert.valid.from).getTime() : null;
  const validTo = cert.valid ? new Date(cert.valid.to).getTime() : null;
  const now = Date.now();

  return {
    subject: subject.commonName ?? subject.organizationName ?? "",
    organisation: subject.organizationName ?? issuer.organizationName ?? null,
    taxNumber:
      (subject.serialNumber ?? "").replace(TAX_NUMBER_PREFIX_PATTERN, "") ||
      ipn.DRFO ||
      ipn.EDRPOU ||
      null,
    issuer: issuer.commonName ?? "",
    validFrom: toIsoDate(cert.valid?.from),
    validTo: toIsoDate(cert.valid?.to),
    isActive:
      validFrom !== null &&
      validTo !== null &&
      now >= validFrom &&
      now < validTo,
    serial: cert.serial ? cert.serial.toString(16).toUpperCase() : null,
    canSign: canUseFor(cert, "sign"),
    hasPrivateKey: Boolean(priv),
  };
};
